/**
 * Push Notification Service
 * Handles FCM Web Push notification registration and management
 */

import { getToken, onMessage, deleteToken } from 'firebase/messaging';
import { initializeMessaging, VAPID_KEY } from '../lib/firebase';
import axios from '@/utils/axios';
import { authError } from '@/utils/authError';


class PushNotificationService {
  constructor() {
    this.messaging = null;
    this.currentToken = null;
    this.initialized = false;
  }

  /**
   * Check if push notifications are supported in this browser
   */
  isSupported() {
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  /**
   * Get current notification permission status
   * @returns {'granted' | 'denied' | 'default'}
   */
  getPermissionStatus() {
    if (!this.isSupported()) {
      return 'unsupported';
    }
    return Notification.permission;
  }

  /**
   * Initialize the service worker and messaging
   * @returns {Promise<boolean>}
   */
  async initialize() {
    if (this.initialized) {
      return true;
    }

    if (!this.isSupported()) {
      console.warn('[PushNotificationService] Push notifications not supported');
      return false;
    }

    try {
      // Register service worker
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      console.log('[PushNotificationService] Service worker registered:', registration.scope);

      // Wait for the service worker to be ready
      await navigator.serviceWorker.ready;

      // Initialize Firebase messaging
      this.messaging = await initializeMessaging();

      if (!this.messaging) {
        console.warn('[PushNotificationService] Firebase messaging not available');
        return false;
      }

      this.initialized = true;
      console.log('[PushNotificationService] Initialized successfully');
      return true;
    } catch (error) {
      console.error('[PushNotificationService] Initialization failed:', error);
      return false;
    }
  }

  /**
   * Request notification permission from the user
   * @returns {Promise<'granted' | 'denied' | 'default'>}
   */
  async requestPermission() {
    if (!this.isSupported()) {
      throw new Error('Push notifications not supported');
    }

    try {
      const permission = await Notification.requestPermission();
      console.log('[PushNotificationService] Permission result:', permission);
      return permission;
    } catch (error) {
      console.error('[PushNotificationService] Permission request failed:', error);
      throw error;
    }
  }

  /**
   * Get FCM token for this browser
   * @returns {Promise<string|null>}
   */
  async getToken() {
    if (!this.initialized || !this.messaging) {
      const success = await this.initialize();
      if (!success) {
        return null;
      }
    }

    if (Notification.permission !== 'granted') {
      console.warn('[PushNotificationService] Notification permission not granted');
      return null;
    }

    try {
      // Get the service worker registration
      const registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');

      if (!registration) {
        console.error('[PushNotificationService] Service worker not registered');
        return null;
      }

      const token = await getToken(this.messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration
      });

      if (token) {
        this.currentToken = token;
        console.log('[PushNotificationService] FCM token obtained');
        return token;
      } else {
        console.warn('[PushNotificationService] No registration token available');
        return null;
      }
    } catch (error) {
      console.error('[PushNotificationService] Error getting token:', error);
      return null;
    }
  }

  /**
   * Send FCM token to server. Auth is via httpOnly cookie (axios already
   * has `withCredentials: true`) so no token argument is needed — the
   * old `authToken` param was a leftover from pre-cookie auth.
   * @returns {Promise<boolean>}
   */
  async sendTokenToServer() {
    const fcmToken = this.currentToken || await this.getToken();

    if (!fcmToken) {
      console.warn('[PushNotificationService] No FCM token to send');
      return false;
    }

    try {
      await axios.post(`/users/me/fcm-token`, {
        fcm_token: fcmToken,
        platform: 'web',
      });
      console.log('[PushNotificationService] Token sent to server');
      return true;
    } catch (error) {
      console.error('[PushNotificationService] Failed to send token:', error);
      return false;
    }
  }

  /**
   * Remove FCM token from server (on logout)
   * @param {string} authToken - User's authentication token
   * @returns {Promise<boolean>}
   */
  async removeTokenFromServer(authToken) {
    const fcmToken = this.currentToken;

    if (!fcmToken) {
      // No token to remove
      return true;
    }

    try {
      await axios.delete(
        `/users/me/fcm-token`,
        {
          data: { fcm_token: fcmToken }
        }
      );
      console.log('[PushNotificationService] Token removed from server');
      this.currentToken = null;
      return true;
    } catch (error) {
      console.error('[PushNotificationService] Failed to remove token:', error);
      return false;
    }
  }

  /**
   * Delete the FCM token locally
   * @returns {Promise<boolean>}
   */
  async deleteLocalToken() {
    if (!this.messaging) {
      return true;
    }

    try {
      await deleteToken(this.messaging);
      this.currentToken = null;
      console.log('[PushNotificationService] Local token deleted');
      return true;
    } catch (error) {
      console.error('[PushNotificationService] Failed to delete local token:', error);
      return false;
    }
  }

  /**
   * Full flow to enable push notifications. Auth is via httpOnly cookie;
   * no auth token argument needed.
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async enablePushNotifications() {
    try {
      // Step 1: Check support
      if (!this.isSupported()) {
        return { success: false, error: authError('pushUnsupported') };
      }

      // Step 2: Initialize service worker
      const initialized = await this.initialize();
      if (!initialized) {
        return { success: false, error: authError('pushInitFailed') };
      }

      // Step 3: Request permission
      const permission = await this.requestPermission();
      if (permission === 'denied') {
        return {
          success: false,
          error: authError('pushPermissionDenied')
        };
      }
      if (permission !== 'granted') {
        return { success: false, error: authError('pushPermissionNotGranted') };
      }

      // Step 4: Get FCM token
      const token = await this.getToken();
      if (!token) {
        return { success: false, error: authError('pushTokenFailed') };
      }

      // Step 5: Send token to server (cookie auth — no token arg needed)
      const sent = await this.sendTokenToServer();
      if (!sent) {
        return { success: false, error: authError('pushRegisterFailed') };
      }

      return { success: true };
    } catch (error) {
      console.error('[PushNotificationService] Enable failed:', error);
      return { success: false, error: error.message || authError('unexpected') };
    }
  }

  /**
   * Set up foreground message handler
   * @param {Function} callback - Called when a message is received in foreground
   * @returns {Function|null} - Unsubscribe function
   */
  onForegroundMessage(callback) {
    if (!this.messaging) {
      console.warn('[PushNotificationService] Messaging not initialized');
      return null;
    }

    return onMessage(this.messaging, (payload) => {
      console.log('[PushNotificationService] Foreground message received:', payload);
      callback(payload);
    });
  }
}

// Export singleton instance
const pushNotificationService = new PushNotificationService();
export default pushNotificationService;
